import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LogIn, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSiteReminders, TIME_CHANGED_EVENT } from '../hooks/useSiteReminders';
import { announceClockOut } from './ClockOutConfirm';

const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    osc.frequency.value = 880;
    osc.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.35);
    navigator.vibrate?.([200, 100, 200]);
  } catch { /* sound not available */ }
};

export const SiteReminder = () => {
  const { authAxios, user } = useAuth();
  const navigate = useNavigate();
  const enabled = !!user && user.role !== 'admin';
  const { reminder, dismiss, position } = useSiteReminders(authAxios, enabled);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (reminder) beep(); }, [reminder]);

  if (!reminder) return null;
  const isIn = reminder.type === 'clock_in';

  const clockOut = async () => {
    setBusy(true);
    try {
      const p = position.current;
      const res = await authAxios.post('/time/clock-out', p ? { latitude: p.lat, longitude: p.lng } : {});
      toast.success('Clocked out');
      window.dispatchEvent(new Event(TIME_CHANGED_EVENT));
      announceClockOut(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clock out');
    } finally {
      setBusy(false);
    }
  };

  const goClockIn = () => { dismiss(); navigate('/dashboard'); };

  return (
    <Dialog open onOpenChange={(o) => !o && dismiss()}>
      <DialogContent className="sm:max-w-md" data-testid={`site-reminder-${reminder.type}`}>
        <DialogHeader>
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-2 ${isIn ? 'bg-emerald-100' : 'bg-amber-100'}`}>
            {isIn ? <LogIn className="w-7 h-7 text-emerald-700" /> : <LogOut className="w-7 h-7 text-amber-700" />}
          </div>
          <DialogTitle className="text-xl">{isIn ? "You haven't clocked in" : "You haven't clocked out"}</DialogTitle>
          <DialogDescription className="text-base">
            {isIn
              ? `You've been at ${reminder.site.name} for 5 minutes. Clock in so your hours count.`
              : `It looks like you've left ${reminder.site.name}. Don't forget to clock out.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={dismiss} data-testid="site-reminder-dismiss">
            {isIn ? 'Not now' : "I'm still working"}
          </Button>
          {isIn ? (
            <Button onClick={goClockIn} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="site-reminder-clock-in">
              Go to Clock In
            </Button>
          ) : (
            <Button onClick={clockOut} disabled={busy} className="bg-amber-600 hover:bg-amber-500 text-white" data-testid="site-reminder-clock-out">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Clock Out Now
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
